import { TenantResolutionError } from './errors';
import { getScheduifySubdomainSlug, normalizeHostname } from './hostname';
import { encodeHostnameForDocumentId, validateTenantId } from './paths';
import { TenantHost, TenantHostKind, TenantHostState, TenantRuntime } from './types';

export type TenantHostRecordReader = (encodedHostname: string) => Promise<unknown | null>;

const HOST_STATES: TenantHostState[] = ['pending', 'active', 'suspended', 'disabled'];
const HOST_KINDS: TenantHostKind[] = ['scheduify-subdomain', 'custom-domain', 'local-subdomain'];

const parseTenantHost = (value: unknown, expectedHostname: string): TenantHost => {
  if (!value || typeof value !== 'object') {
    throw new TenantResolutionError('INVALID_HOST_RECORD', 'Tenant host record is malformed.');
  }
  const record = value as Record<string, unknown>;
  const hostname = typeof record.hostname === 'string' ? normalizeHostname(record.hostname) : '';
  const tenantId = typeof record.tenantId === 'string' ? validateTenantId(record.tenantId) : '';
  const state = record.state as TenantHostState;
  const kind = record.kind as TenantHostKind;

  if (
    hostname !== expectedHostname ||
    !tenantId ||
    !HOST_STATES.includes(state) ||
    !HOST_KINDS.includes(kind) ||
    typeof record.isPrimary !== 'boolean'
  ) {
    throw new TenantResolutionError('INVALID_HOST_RECORD', 'Tenant host record is malformed.');
  }

  return {
    tenantId,
    hostname,
    slug: typeof record.slug === 'string' && record.slug ? record.slug : undefined,
    state,
    kind,
    isPrimary: record.isPrimary,
    createdAt: record.createdAt,
    verifiedAt: record.verifiedAt,
  };
};

export const resolveTenantByHostnameRecord = async (
  hostnameInput: string,
  readRecord: TenantHostRecordReader,
  // Preview booking on a business still in setup, whose address is still pending.
  options: { allowPending?: boolean } = {}
): Promise<TenantRuntime> => {
  const hostname = normalizeHostname(hostnameInput);
  const record = await readRecord(encodeHostnameForDocumentId(hostname));
  if (record === null) {
    throw new TenantResolutionError('UNKNOWN_HOST', `No tenant is registered for ${hostname}.`);
  }

  const host = parseTenantHost(record, hostname);
  if (host.state === 'pending' && !options.allowPending) {
    throw new TenantResolutionError('PENDING_HOST', `Tenant host ${hostname} is not active yet.`);
  }
  if (host.state === 'suspended') {
    throw new TenantResolutionError('SUSPENDED_HOST', `Tenant host ${hostname} is suspended.`);
  }
  if (host.state === 'disabled') {
    throw new TenantResolutionError('DISABLED_HOST', `Tenant host ${hostname} is disabled.`);
  }

  return {
    tenantId: host.tenantId,
    hostname,
    slug: host.slug ?? getScheduifySubdomainSlug(hostname),
  };
};
